/**
 * FullStacked Cloud Console - Plan, quotas and usage (auth GET /v1/billing, POST /v1/billing/sync;
 * authentication.md "6. Clerk Billing"). The access profile's `data.billing` (snake_case) is the
 * fallback while /v1/billing cannot be read. Every number the Console shows comes from here.
 */

import type { FreezeReason, Plan, UserAccess } from "../../../src/common/types.ts";
import { send, type ApiContext } from "./client.ts";
import { isRecord, num, pickList, str, strList, type Raw } from "./normalize.ts";

export interface BillingQuotas {
    projects: number | null;
    dataInstances: number | null;
    edges: number | null;
    tunnelsPerEdge: number | null;
    storageBytes: number | null;
    monthlyEdgeTransferBytes: number | null;
    monthlyDataTransferBytes: number | null;
}

export interface BillingUsage {
    projects: number;
    dataInstances: number;
    edges: number;
    storageBytes: number;
    monthlyEdgeTransferBytes: number;
    monthlyDataTransferBytes: number;
    /** Current UTC month of the transfer counters ("2026-10"). */
    transferMonth: string;
}

export type BillingResourceType = "project" | "data" | "edge" | "tunnel";

export interface BillingAsset {
    resourceType: BillingResourceType;
    resourceId: string;
    storageBytes: number;
    sponsored: boolean;
    frozen: boolean;
    freezeReason: FreezeReason | null;
}

export interface SponsoredAsset {
    resourceType: BillingResourceType;
    resourceId: string;
    /** Accepted Pro members sponsoring it (empty when sponsored through a project link). */
    sponsorUserIds: string[];
}

export interface ProPlanOffer {
    name: string;
    /** Minor units (cents), null when Clerk Billing reports no price. */
    amount: number | null;
    currency: string | null;
    interval: string | null;
}

export interface BillingInfo {
    plan: Plan;
    /** Display names of the plans in Clerk Billing (labels and badges follow them). */
    planNames: Record<Plan, string>;
    isOperator: boolean;
    overQuotaFreeze: boolean;
    edgeTransferExhausted: boolean;
    dataTransferExhausted: boolean;
    quotaExceededSince: string | null;
    quotas: BillingQuotas;
    usage: BillingUsage;
    assets: BillingAsset[];
    sponsoredAssets: SponsoredAsset[];
    pendingInvitations: number;
    proPlan: ProPlanOffer | null;
    /** The Pro subscription: canceled, and until when it stays active (null on Base). */
    subscription: { canceled: boolean; activeUntil: string | null } | null;
}

const QUOTA_KEYS: Array<[keyof BillingQuotas, string]> = [
    ["projects", "projects"],
    ["dataInstances", "data_instances"],
    ["edges", "edges"],
    ["tunnelsPerEdge", "tunnels_per_edge"],
    ["storageBytes", "storage_bytes"],
    ["monthlyEdgeTransferBytes", "monthly_edge_transfer_bytes"],
    ["monthlyDataTransferBytes", "monthly_data_transfer_bytes"],
];

const USAGE_KEYS: Array<[Exclude<keyof BillingUsage, "transferMonth">, string]> = [
    ["projects", "projects"],
    ["dataInstances", "data_instances"],
    ["edges", "edges"],
    ["storageBytes", "storage_bytes"],
    ["monthlyEdgeTransferBytes", "monthly_edge_transfer_bytes"],
    ["monthlyDataTransferBytes", "monthly_data_transfer_bytes"],
];

const RESOURCE_TYPES: readonly BillingResourceType[] = ["project", "data", "edge", "tunnel"];

function resourceType(r: Raw): BillingResourceType {
    const t = str(r, "resourceType", "resource_type") as BillingResourceType | undefined;
    return t && RESOURCE_TYPES.includes(t) ? t : "project";
}

function quotasOf(raw: unknown): BillingQuotas {
    const r = isRecord(raw) ? raw : {};
    const out = {} as BillingQuotas;
    for (const [camel, snake] of QUOTA_KEYS) out[camel] = num(r, camel, snake) ?? null;
    return out;
}

/** The UTC month of `at` ("2026-10"). */
export function utcMonth(at: Date = new Date()): string {
    return at.toISOString().slice(0, 7);
}

function usageOf(raw: unknown, now?: Date): BillingUsage {
    const r = isRecord(raw) ? raw : {};
    const out = { transferMonth: str(r, "transferMonth", "transfer_month") ?? utcMonth(now) };
    for (const [camel, snake] of USAGE_KEYS) {
        (out as Record<string, unknown>)[camel] = num(r, camel, snake) ?? 0;
    }
    return out as BillingUsage;
}

function freezeReason(v: unknown): FreezeReason | null {
    return v === "count" || v === "storage" ? v : null;
}

function proPlanOf(raw: unknown): ProPlanOffer | null {
    if (!isRecord(raw)) return null;
    return {
        name: str(raw, "name") ?? "Pro",
        amount: num(raw, "amount") ?? null,
        currency: str(raw, "currency") ?? null,
        interval: str(raw, "interval") ?? null,
    };
}

export function normalizeBilling(raw: unknown, now?: Date): BillingInfo {
    const r = isRecord(raw) && isRecord(raw.data) ? raw.data : isRecord(raw) ? raw : {};
    const names = isRecord(r.planNames) ? r.planNames : isRecord(r.plan_names) ? r.plan_names : {};
    return {
        plan: str(r, "plan") === "pro" ? "pro" : "base",
        planNames: { base: str(names, "base") ?? "Base", pro: str(names, "pro") ?? "Pro" },
        isOperator: r.isOperator === true || r.is_operator === true,
        overQuotaFreeze: r.overQuotaFreeze === true || r.over_quota_freeze === true,
        edgeTransferExhausted:
            r.edgeTransferExhausted === true || r.edge_transfer_exhausted === true,
        dataTransferExhausted:
            r.dataTransferExhausted === true || r.data_transfer_exhausted === true,
        quotaExceededSince: str(r, "quotaExceededSince", "quota_exceeded_since") ?? null,
        quotas: quotasOf(r.quotas),
        usage: usageOf(r.usage, now),
        assets: pickList(r, "assets").map((a) => ({
            resourceType: resourceType(a),
            resourceId: str(a, "resourceId", "resource_id") ?? "",
            storageBytes: num(a, "storageBytes", "storage_bytes") ?? 0,
            sponsored: a.sponsored === true,
            frozen: a.frozen === true,
            freezeReason: freezeReason(a.freezeReason ?? a.freeze_reason),
        })),
        sponsoredAssets: pickList(r, "sponsoredAssets", "sponsored_assets").map((a) => ({
            resourceType: resourceType(a),
            resourceId: str(a, "resourceId", "resource_id") ?? "",
            sponsorUserIds: strList(a, "sponsorUserIds", "sponsor_user_ids"),
        })),
        pendingInvitations: num(r, "pendingInvitations", "pending_invitations") ?? 0,
        proPlan: proPlanOf(r.proPlan ?? r.pro_plan),
        subscription: isRecord(r.subscription)
            ? {
                  canceled: r.subscription.canceled === true,
                  activeUntil: str(r.subscription, "activeUntil", "active_until") ?? null,
              }
            : null,
    };
}

/**
 * `data.billing` of GET /api/v1/user/access: plan, quotas and usage only (no assets, price or
 * invitations). Null when auth sent none.
 */
export function billingFromAccess(access: UserAccess | null, now?: Date): BillingInfo | null {
    if (!access?.billing) return null;
    const isOperator = access.global_permissions?.includes("authorities:create") ?? false;
    return { ...normalizeBilling(access.billing, now), isOperator };
}

export async function getBilling(ctx: ApiContext): Promise<BillingInfo> {
    return normalizeBilling(await send(ctx, { service: "auth", path: "/v1/billing" }));
}

/** Refreshes the plan from Clerk Billing (1 call / 30 s, else 429 RATE_LIMITED). */
export async function syncBilling(ctx: ApiContext): Promise<BillingInfo> {
    return normalizeBilling(
        await send(ctx, { service: "auth", method: "POST", path: "/v1/billing/sync", body: {} })
    );
}
